import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Platform,
  PanResponder,
} from "react-native";
import ToothFairyModal from "./ToothFairyModal";
import ToothFairyButton from "./ToothFairyButton";

const { width } = Dimensions.get("window");
const isWeb = Platform.OS === "web";

const ColorPickerModal = ({
  isVisible = false,
  setIsModalOpen,
  onClose,
  initialColor = "#000000",
  onColorSelect,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const slContainerRef = useRef(null);
  const hueContainerRef = useRef(null);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const [isDraggingSL, setIsDraggingSL] = useState(false);
  const slDimensionsRef = useRef({ width: 280, height: 150 });
  const hueWidthRef = useRef(280);

  useEffect(() => {
    if (initialColor) {
      const rgb = hexToRgb(initialColor);
      if (rgb) {
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
        setHue(hsl.h);
        setSaturation(hsl.s);
        setLightness(hsl.l);
        setSelectedColor(initialColor);
      }
    }
  }, [initialColor, isVisible]);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  };

  const hslToHex = useCallback((h, s, l) => {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }, []);

  useEffect(() => {
    const hex = hslToHex(hue, saturation, lightness);
    setSelectedColor(hex);
  }, [hue, saturation, lightness, hslToHex]);

  const updateSLFromPosition = useCallback((clientX, clientY) => {
    if (!slContainerRef.current && isWeb) return;

    const rect = slContainerRef.current?.getBoundingClientRect?.() || {
      left: 0,
      top: 0,
      width: 280,
      height: 150,
    };
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const clampedX = Math.max(0, Math.min(x, rect.width));
    const clampedY = Math.max(0, Math.min(y, rect.height));

    const newSaturation = Math.round((clampedX / rect.width) * 100);
    const newLightness = Math.round(100 - (clampedY / rect.height) * 100);

    setSaturation(newSaturation);
    setLightness(newLightness);
  }, []);

  const updateHueFromPosition = useCallback((clientX) => {
    if (!hueContainerRef.current && isWeb) return;

    const rect = hueContainerRef.current?.getBoundingClientRect?.() || {
      left: 0,
      width: 280,
    };
    const x = clientX - rect.left;

    const clampedX = Math.max(0, Math.min(x, rect.width));
    const newHue = Math.round((clampedX / rect.width) * 360);

    setHue(newHue);
  }, []);

  useEffect(() => {
    if (!isWeb || !isDraggingSL) return;

    const handleMouseMove = (e) => {
      updateSLFromPosition(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      setIsDraggingSL(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingSL, updateSLFromPosition]);

  useEffect(() => {
    if (!isWeb || !isDraggingHue) return;

    const handleMouseMove = (e) => {
      updateHueFromPosition(e.clientX);
    };

    const handleMouseUp = () => {
      setIsDraggingHue(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingHue, updateHueFromPosition]);

  const handleHueStart = useCallback(
    (e) => {
      setIsDraggingHue(true);
      if (isWeb && e.clientX !== undefined) {
        updateHueFromPosition(e.clientX);
      }
    },
    [updateHueFromPosition],
  );

  const handleSLStart = useCallback(
    (e) => {
      setIsDraggingSL(true);
      if (isWeb && e.clientX !== undefined) {
        updateSLFromPosition(e.clientX, e.clientY);
      }
    },
    [updateSLFromPosition],
  );

  const handleTouchSL = useCallback(
    (e) => {
      const touch = e.nativeEvent?.touches?.[0];
      if (touch) {
        updateSLFromPosition(touch.clientX, touch.clientY);
      }
    },
    [updateSLFromPosition],
  );

  const handleTouchHue = useCallback(
    (e) => {
      const touch = e.nativeEvent?.touches?.[0];
      if (touch) {
        updateHueFromPosition(touch.clientX);
      }
    },
    [updateHueFromPosition],
  );

  const handleConfirm = () => {
    onColorSelect?.(selectedColor);
    onClose?.();
  };

  const presetColors = [
    "#FF0000",
    "#FF8000",
    "#FFFF00",
    "#80FF00",
    "#00FF00",
    "#00FF80",
    "#00FFFF",
    "#0080FF",
    "#0000FF",
    "#8000FF",
    "#FF00FF",
    "#FF0080",
    "#000000",
    "#333333",
    "#666666",
    "#999999",
    "#CCCCCC",
    "#FFFFFF",
  ];

  const styles = StyleSheet.create({
    container: {
      padding: 20,
    },
    title: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 20,
      color: currentTheme?.theme?.font_color,
      textAlign: "center",
    },
    slPickerContainer: {
      width: 280,
      height: 150,
      borderRadius: 8,
      marginBottom: 20,
      alignSelf: "center",
      overflow: "hidden",
    },
    slPicker: {
      width: "100%",
      height: "100%",
    },
    huePickerContainer: {
      width: 280,
      height: 20,
      borderRadius: 10,
      marginBottom: 20,
      alignSelf: "center",
      overflow: "hidden",
    },
    huePicker: {
      width: "100%",
      height: "100%",
    },
    hueGradient: {
      width: "100%",
      height: "100%",
      flexDirection: "row",
    },
    colorPreviewContainer: {
      alignItems: "center",
      marginBottom: 20,
    },
    colorPreview: {
      width: 60,
      height: 60,
      borderRadius: 30,
      borderWidth: 2,
      borderColor: currentTheme?.theme?.border_color,
      marginBottom: 10,
    },
    colorText: {
      fontSize: 16,
      color: currentTheme?.theme?.font_color,
      fontWeight: "500",
    },
    presetsContainer: {
      marginBottom: 20,
    },
    presetsTitle: {
      fontSize: 14,
      color: currentTheme?.theme?.font_color,
      marginBottom: 10,
      marginLeft: 5,
    },
    presetsGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    presetColor: {
      width: 30,
      height: 30,
      borderRadius: 15,
      margin: 5,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
    selectedPreset: {
      borderWidth: 2,
      borderColor: currentTheme?.theme?.primary,
    },
    buttonsContainer: {
      flexDirection: "row",
      justifyContent: "center",
      gap: 10,
    },
    cursor: {
      position: "absolute",
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: "#FFFFFF",
      backgroundColor: "transparent",
    },
  });

  const slCursorPosition = {
    left: (saturation / 100) * 280 - 10,
    top: ((100 - lightness) / 100) * 150 - 10,
  };

  const hueCursorPosition = {
    left: (hue / 360) * 280 - 6,
  };

  return (
    <ToothFairyModal
      isModalOpen={isVisible}
      setIsModalOpen={setIsModalOpen}
      closeModal={onClose}
      isCloseButtonVisible={false}
      modalStyle={{
        maxWidth: 340,
        maxHeight: 587,
      }}
    >
      <View style={styles.container}>
        <View
          ref={isWeb ? slContainerRef : null}
          style={styles.slPickerContainer}
          onMouseDown={handleSLStart}
          onTouchStart={handleSLStart}
          onTouchMove={handleTouchSL}
        >
          <View
            style={[
              styles.slPicker,
              {
                backgroundColor: `hsl(${hue}, 100%, 50%)`,
              },
            ]}
          >
            {isWeb && (
              <div
                ref={slContainerRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(to right, white, transparent),
                               linear-gradient(to top, black, transparent)`,
                  backgroundBlendMode: "multiply",
                  cursor: "crosshair",
                }}
              />
            )}
            <View style={[styles.cursor, slCursorPosition]} />
          </View>
        </View>

        <View
          ref={isWeb ? hueContainerRef : null}
          style={styles.huePickerContainer}
          onMouseDown={handleHueStart}
          onTouchStart={handleHueStart}
          onTouchMove={handleTouchHue}
        >
          <View style={styles.huePicker}>
            {isWeb ? (
              <div
                ref={hueContainerRef}
                style={{
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
              />
            ) : (
              <View
                style={{
                  width: "100%",
                  height: "100%",
                  flexDirection: "row",
                }}
              >
                {[...Array(7)].map((_, i) => (
                  <View
                    key={i}
                    style={{
                      flex: 1,
                      height: "100%",
                    }}
                  />
                ))}
              </View>
            )}
            <View
              style={[
                styles.cursor,
                { width: 12, height: 20, borderRadius: 6 },
                hueCursorPosition,
                { top: 0 },
              ]}
            />
          </View>
        </View>

        <View style={styles.colorPreviewContainer}>
          <View
            style={[styles.colorPreview, { backgroundColor: selectedColor }]}
          />
          <AppText style={styles.colorText}>
            {selectedColor?.toUpperCase()}
          </AppText>
        </View>

        <View style={styles.presetsContainer}>
          <AppText style={styles.presetsTitle}>Preset Colors</AppText>
          <View style={styles.presetsGrid}>
            {presetColors.map((color) => (
              <TouchableOpacity
                key={color}
                style={[
                  styles.presetColor,
                  { backgroundColor: color },
                  selectedColor?.toUpperCase() === color &&
                    styles.selectedPreset,
                ]}
                onPress={() => {
                  const rgb = hexToRgb(color);
                  if (rgb) {
                    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
                    setHue(hsl.h);
                    setSaturation(hsl.s);
                    setLightness(hsl.l);
                  }
                }}
              />
            ))}
          </View>
        </View>

        <View style={styles.buttonsContainer}>
          <ToothFairyButton
            title="Cancel"
            fontColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.mediumGrey}
            isSecondary
            onPress={onClose}
            style={{ marginHorizontal: 5 }}
          />
          <ToothFairyButton
            title="Select"
            onPress={handleConfirm}
            style={{ marginHorizontal: 5 }}
          />
        </View>
      </View>
    </ToothFairyModal>
  );
};

export default ColorPickerModal;
